import { CollectUserView, DocumentIntakeSourceType } from "generated/graphql";
import type { DocumentIntakeAugmented } from "redux/slices/case";
import { parsePuidAndPlatform } from "puid";
import { UserInfo } from "Backend";
import { Api, DriveFolderUpload } from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import { CollectBlack, PatternLogoBlack } from "components/Icons";
import theme from "theme";

interface IntakeUploadedByProps {
  intake: DocumentIntakeAugmented;
  users: UserInfo[];
  collectUsers: CollectUserView[];
}

const iconSize = 16;
const iconColor = theme.palette.grey["700"];

const getUserText = (
  puid: string | null | undefined,
  users: UserInfo[],
  collectUsers: CollectUserView[],
  sourceType: DocumentIntakeSourceType | null | undefined
): { text: string | undefined; tooltip: string | undefined } => {
  if (sourceType === "transfer") {
    return {
      text: "Bulk Upload",
      tooltip: undefined,
    };
  }
  if (puid === null || puid === undefined) {
    return { text: undefined, tooltip: undefined };
  }
  const { platform, value } = parsePuidAndPlatform(puid);
  const userId = value;
  if (platform === "pat") {
    const user = users.find((u) => puid && u.id === userId);
    return {
      text: [user?.firstName, user?.lastName].filter((x) => x).join(" "),
      tooltip: user?.email,
    };
  } else if (platform === "collect") {
    const collectUser = collectUsers.find((c) => puid && c.id === userId);
    return {
      text: [collectUser?.firstName, collectUser?.lastName].filter((x) => x).join(" "),
      tooltip: collectUser?.email,
    };
  } else {
    return { text: undefined, tooltip: undefined };
  }
};

const getSourceTypeIconAltText = (sourceType: DocumentIntakeSourceType): string => {
  switch (sourceType) {
    case DocumentIntakeSourceType.CollectApp:
      return "Uploaded via Collect";
    case DocumentIntakeSourceType.ExternalApi:
      return "Uploaded via API";
    case DocumentIntakeSourceType.Transfer:
      return "Uploaded via Bulk Upload";
    case DocumentIntakeSourceType.PatternApp:
      return "Uploaded via Pattern";
    case DocumentIntakeSourceType.SmartAdvocate:
      return "Uploaded via Smart Advocate";
  }
};

const SourceTypeIcon = (props: { sourceType: DocumentIntakeSourceType }) => {
  switch (props.sourceType) {
    case DocumentIntakeSourceType.CollectApp:
      return <CollectBlack size={iconSize} htmlColor={iconColor} />;
    case DocumentIntakeSourceType.ExternalApi:
      return <Api sx={{ height: iconSize, width: iconSize, color: iconColor }} />;
    case DocumentIntakeSourceType.Transfer:
      return <DriveFolderUpload sx={{ height: iconSize, width: iconSize, color: iconColor }} />;
    case DocumentIntakeSourceType.PatternApp:
      return <PatternLogoBlack size={iconSize} htmlColor={iconColor} />;
    case DocumentIntakeSourceType.SmartAdvocate:
      return <Api sx={{ height: iconSize, width: iconSize, color: iconColor }} />;
  }
};

const IntakeUploadedBy = (props: IntakeUploadedByProps) => {
  const sourceType = props.intake.sourceType;
  const puid = props.intake.createdByPuid;
  const users = props.users;
  const collectUsers = props.collectUsers;
  const userInfo = getUserText(puid, users, collectUsers, sourceType);

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", height: iconSize }}>
      {sourceType && (
        <Tooltip title={getSourceTypeIconAltText(sourceType)}>
          <span>
            <SourceTypeIcon sourceType={sourceType} />
          </span>
        </Tooltip>
      )}
      <Tooltip title={userInfo.tooltip && userInfo.tooltip}>
        <span>{userInfo.text && userInfo.text}</span>
      </Tooltip>
    </Box>
  );
};

export default IntakeUploadedBy;
